// `titles-with-classes.title-class-ux.typeahead.tpl.html` — the Position field's own
// suggestion panel.
//
// ⚠️ THE SAME ITEMS, SPLIT IN TWO. `titleClass: true` rows are "Extended Categories" and
// everything else — plus the add-new row, which appears in the second section however it is
// classed — is "Specific Titles". The flat list this port rendered lost the grouping, both
// headers and the amber class pills; `_search.page.scss:118` has carried the rules for all of
// it since the stylesheet was ported, selecting
// `.autocomplete.title-with-classes-title-class-ux-auto-complete .recommendation-section`.
//
// ⚠️ AND THE ADD-NEW ROW IS QUOTED. `'"' + highlight(item) + '"'` — it is how the user sees
// that this one is their own words rather than a known title.
import type { ReactElement } from 'react'
import type { SuggestionPanelView, Tag } from '../common/TagsInput.tsx'

/** `filterTitlesToNonClassesOrAddNew` (`search.page.component.ng1.ts:434`). */
export function isSpecificTitle(tag: Tag): boolean {
  return tag.source?.['titleClass'] !== true || tag.addNew === true
}

function Section({
  className,
  title,
  items,
  view,
  itemClassName,
}: {
  className: string
  title: string
  items: readonly Tag[]
  view: SuggestionPanelView
  itemClassName?: string
}): ReactElement | null {
  if (items.length === 0) return null
  return (
    <div className={`${className} recommendation-section`}>
      <label className="recommendation-section-title">
        <span>{title}</span>
        <i className="cursor-pointer info-btn fa fa-info-circle" />
      </label>
      <ul className="suggestion-list">
        {items.map((tag) => {
          const index = view.items.indexOf(tag)
          return (
            <li
              className={['suggestion-item', itemClassName, 'skills-typeahead', index === view.highlighted ? 'selected' : '']
                .filter(Boolean)
                .join(' ')}
              key={tag.text}
              // `mousedown`, not `click`: the input's blur would close the panel first.
              onMouseDown={(event) => {
                event.preventDefault()
                view.onPick(tag)
              }}
              onMouseEnter={() => view.onHighlight(index)}
            >
              <span className="suggestion-item-inner">
                {tag.addNew === true ? (
                  <span>
                    {'"'}
                    {view.highlight(tag.text)}
                    {'"'}
                  </span>
                ) : (
                  <span>{view.highlight(tag.text)}</span>
                )}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function TitleSuggestionPanel(view: SuggestionPanelView): ReactElement {
  const categories = view.items.filter((tag) => tag.source?.['titleClass'] === true && tag.addNew !== true)
  const titles = view.items.filter(isSpecificTitle)
  return (
    <div className="autocomplete nicer-scroller four-px-scroll title-with-classes-title-class-ux-auto-complete">
      <Section
        className="categories"
        title="Extended Categories"
        items={categories}
        view={view}
        itemClassName="suggestion-item-title-class"
      />
      <Section className="titles" title="Specific Titles" items={titles} view={view} />
    </div>
  )
}
